<template>
  <div class="container-page">
    <div>
      <p class="title-inner">Карточка документа</p>
    </div>
    <div>
      <div class="row">
        <div class="form-group required">
          <ComponentInput
            v-model="card.name"
            label="Название"
            required
            :maxLength="1000"
            placeholder="Введите название"
          />
        </div>
      </div>
      <div class="row" v-if="!card.documentId">
        <div class="form-group">
          <label class="form-label">Файл</label>
          <FileUpload width="100%" :entity="card" @deleteFile="removeFile" propName="file" @uploaded="fileUploaded" />
        </div>
      </div>
      <div class="row" v-if="!card.fileId && (card.documentId || hasAdminAccess)">
        <label class="form-label">Документ</label>
        <div class="form-group d-flex align-items-center">
          <div class="input-wrapper">
            <ComponentInput
              :select2Options="documentList"
              v-model="card.documentId"
              type="select2"
              :select2Settings="select2SettingsWithAjax"
              :disabled="!hasAdminAccess"
            />
          </div>
          <div class="icon-wrapper" v-if="card.documentHasRtf">
            <IconComponent @click="downloadDoc()" name="download_transparent" title="Скачать документ" />
          </div>
          <div class="icon-wrapper" v-if="card.documentUniversalRef">
            <IconComponent @click="openDocumentRef()" name="docsIcon" title="Открыть документ" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="form-group required">
          <label for="txtComment" class="form-label">Комментарий</label>
          <textarea v-model="card.comment" id="txtComment" class="form-control" rows="3"></textarea>
        </div>
      </div>
      <div class="row mt-10">
        <CheckboxInput v-model="card.isCopy" label="Копия" />
      </div>
      <div class="row mt-10">
        <CheckboxInput v-model="card.isActive" label="Запись активна" />
      </div>
    </div>
    <div class="mb-16">
      <ButtonComponent mod="gradient-bg" class="mr-8" @click="save">Сохранить</ButtonComponent>
      <ButtonComponent mod="border" @click="close">Отмена</ButtonComponent>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex';
  import { useVuelidate } from '@vuelidate/core';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import FileUpload from '@/common/components/FileUpload.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import CheckboxInput from '@/common/components/redesigned/CheckboxInput.vue';
  import Constants from '@/common/constants';
  import runtimeConfig from '@/common/runtime-config';
  import IconComponent from '@/common/ui/IconComponent.vue';

  import API from '../../navigator/api/document';
  import { NAVIGATOR_STEP_CARD_MUTATIONS_NAME } from '../store/stepCards';

  export default {
    name: 'DocumentCard',
    components: { CheckboxInput, FileUpload, ButtonComponent, ComponentInput, IconComponent },
    data() {
      return {
        id: this.$route.params.id,
        redirectURL: this.$route.query.redirectURL,
        card: {},
        documentList: [],
        select2SettingsWithAjax: {
          language: Constants.langRu,
          allowClear: true,
          minimumInputLength: 3,
          ajax: {
            beforeSend: (xhr) => {
              const token = this.$store.getters['auth/oidc/getAccessToken'];
              if (token) {
                xhr.setRequestHeader('Authorization', 'Bearer ' + token);
              }
            },
            delay: 500,
            url: runtimeConfig.apiBaseUrl + '/api/navigatorDocument/searchExternal',
            dataType: 'json',
            data: function (params) {
              var query = {
                query: params.term,
              };
              return query;
            },
            processResults: function (responseData) {
              let data = responseData.map((item) => {
                return {
                  text: item.title,
                  id: item.id,
                };
              });
              const selectData = data.sort((a, b) => a.text.localeCompare(b.text));
              return {
                results: selectData,
              };
            },
            cache: true,
          },
        },
        isSaveDraftCard: false,
      };
    },
    created() {
      this.loadCard();
    },
    unmounted() {
      if (!this.isSaveDraftCard) {
        this.setDraftStepCard(null);
      }
    },
    computed: {
      ...mapGetters(['hasAdminAccess']),
    },
    methods: {
      ...mapMutations({
        setDraftStepCard: NAVIGATOR_STEP_CARD_MUTATIONS_NAME.setDraftStepCard,
      }),
      loadCard() {
        if (this.id > 0) {
          API.find(this.id).then((response) => {
            this.card = response.data;
            if (this.card.documentId) this.loadDocument();
          });
        }
      },
      loadDocument() {
        this.documentList.push({ id: this.card.documentId, text: this.card.documentTitle });
      },
      async save() {
        try {
          this.v$.$touch();
          if (this.v$.$invalid) {
            Constants.alert.fire('Ошибка', 'Проверьте правильность заполнения полей', 'error');
            return;
          }

          const result = await API.update(this.card);

          Constants.alert.fire('Сохранение', Constants.commonSuccess, 'success');

          if (this.redirectURL) {
            let newRequiredDocumentId;

            if (result.data.isActive) newRequiredDocumentId = result.data.id;
            this.isSaveDraftCard = true;
            this.$router.push({ path: this.redirectURL, query: { newRequiredDocumentId } });
            return;
          }

          if (this.card.id) {
            this.$router.push({ name: 'NavigatorDocumentCard', params: { id: this.card.id, action: 'edit' } });
          } else {
            this.$router.push({ name: 'NavigatorDocuments' });
          }
        } catch (e) {
          Constants.alert.fire('Сохранение', Constants.commonError, 'error');
        }
      },
      fileUploaded(e) {
        this.card.file = e.data;
        this.card.fileId = e.data ? e.data.id : null;
      },
      removeFile() {
        this.card.file = {};
        this.card.fileId = null;
      },
      close() {
        if (this.redirectURL) {
          this.isSaveDraftCard = true;
          this.$router.push(this.redirectURL);
          return;
        }

        this.$router.push({ name: 'NavigatorDocuments' });
      },
      downloadDoc() {
        API.getRtf(this.card.documentId);
      },
      openDocumentRef() {
        window.open(this.card.documentUniversalRef, '_blank');
      },
    },
    setup: () => ({ v$: useVuelidate() }),
    validations() {
      return {
        card: {},
      };
    },
  };
</script>

<style lang="scss" scoped>
  .form-label {
    font-weight: 400 !important;
    font-size: 14px;
    color: #231f20;
    line-height: 160%;
  }
  .icon-wrapper {
    margin: 5px 10px;
    & svg {
      stroke: black;
      & path {
        stroke: black;
      }
    }
  }
  .icon-wrapper:deep(svg path) {
    stroke: black;
    color: black;
  }
  .input-wrapper {
    min-width: 200px;
    width: 100%;
  }
</style>
